import { cn } from "@/utils/theme";
import { Button, Icon, Input, Switch } from "@elaniintc/goes-design-system";
import { useRef, useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useOnClickOutside } from "usehooks-ts";

interface EditableCellProps {
  rowIndex: number;
  colName: string;
  type: string;
  stripped?: boolean;
  isSelected?: boolean;
  shouldRenderRightBorder?: boolean;
  onAddData: (
    columnId: string,
    value: string | boolean | number,
    registerId: string,
  ) => void;
  canEditRegistry?: boolean;
  canCreateRegistry: boolean;
}

const getCellBackground = (isSelected: boolean, stripped?: boolean) => {
  if (isSelected) return "bg-surface-action-accent-quiet";
  if (stripped) return "bg-neutral-object-accent-silent";
  return "bg-neutral-object";
};

const cellClassName = (
  isSelected: boolean,
  stripped: boolean | undefined,
  shouldRenderRightBorder: boolean,
  borderRightCondition = true,
) =>
  cn(
    "border-b border-neutral p-3 text-sm border-x max-w-50 truncate text-body-loud",
    getCellBackground(isSelected, stripped),
    borderRightCondition && shouldRenderRightBorder && "border-r-0",
  );

const parseValueByType = (value: string, type: string): string | number => {
  if (type === "INTEGER") return Number.parseInt(value, 10);
  if (type === "FLOAT") return Number.parseFloat(value);
  return value;
};

const ReadOnlyCell = ({
  currentValue,
  className,
}: {
  currentValue: string;
  className: string;
}) => (
  <td className={className}>
    {currentValue ? (
      <span className="text-body-loud">{currentValue}</span>
    ) : (
      <span className="text-on-disabled font-normal">—</span>
    )}
  </td>
);

const BooleanCell = ({
  currentValue,
  rowIndex,
  colName,
  canEditRegistry,
  onAddData,
  id,
  className,
}: {
  currentValue: boolean;
  rowIndex: number;
  colName: string;
  canEditRegistry?: boolean;
  onAddData: EditableCellProps["onAddData"];
  id: string;
  className: string;
}) => (
  <td className={className}>
    <Switch
      id={`switch-${rowIndex}-${colName}`}
      checked={currentValue}
      onCheckedChange={(checked) => {
        if (!canEditRegistry) return;
        onAddData(colName, checked ? 1 : 0, id);
      }}
    />
  </td>
);

const EditableCell = ({
  type,
  colName,
  rowIndex,
  stripped,
  isSelected = false,
  shouldRenderRightBorder = true,
  canEditRegistry,
  canCreateRegistry,
  onAddData,
}: EditableCellProps) => {
  const { watch } = useFormContext();
  const [currentValue, id] = watch([
    `rows.${rowIndex}.${colName}`,
    `rows.${rowIndex}.id`,
  ]);

  const isUnsavedAvailableToEdit =
    canCreateRegistry && currentValue !== null && !id;

  const [state, setState] = useState<"idle" | "edit">(
    currentValue === "" ? "edit" : "idle",
  );

  const tdClassName = cellClassName(isSelected, stripped, shouldRenderRightBorder);

  if (colName === "id") {
    return <ReadOnlyCell currentValue={currentValue} className={tdClassName} />;
  }

  if (type === "BOOLEAN") {
    return (
      <BooleanCell
        currentValue={currentValue}
        rowIndex={rowIndex}
        colName={colName}
        canEditRegistry={canEditRegistry}
        onAddData={onAddData}
        id={id}
        className={cn(
          "border-b border-neutral p-3 text-sm border-x",
          getCellBackground(isSelected, stripped),
          !shouldRenderRightBorder && "border-r-0",
        )}
      />
    );
  }

  if (state === "idle") {
    return (
      <td
        className={tdClassName}
        onClick={() => {
          if (canEditRegistry || isUnsavedAvailableToEdit) {
            setState("edit");
          }
        }}
      >
        {currentValue && <span className="text-body-loud">{currentValue}</span>}
        {currentValue === null && (
          <span className="text-on-disabled font-normal">
            {type === "STRING" ? "Escribe un texto" : "Escribe un número"}
          </span>
        )}
      </td>
    );
  }

  const isNumber = type === "INTEGER" || type === "FLOAT";
  return (
    <EditableTextField
      setState={setState}
      value={isNumber ? currentValue?.toString() : currentValue}
      isNumberInput={isNumber}
      isDecimalInput={type === "FLOAT"}
      colName={colName}
      placeholder={isNumber ? "Escribe un número..." : undefined}
      rowIndex={rowIndex}
      onAddData={(columnId, value) => {
        onAddData(columnId, parseValueByType(value, type), id);
      }}
    />
  );
};

export default EditableCell;

interface EditableTextFieldProps {
  rowIndex?: number;
  colName?: string;
  value?: string;
  isNumberInput?: boolean;
  isDecimalInput?: boolean;
  placeholder?: string;
  setState: (state: "idle" | "edit") => void;
  onAddData: (columnId: string, value: string) => void;
}

const EditableTextField = ({
  setState,
  value,
  colName,
  rowIndex,
  isNumberInput,
  isDecimalInput,
  placeholder,
  onAddData,
}: EditableTextFieldProps) => {
  const [tempValue, setTempValue] = useState(value || "");
  const { setValue } = useFormContext();

  const [containerWidth, setContainerWidth] = useState(0);
  const ref = useRef<HTMLTableCellElement>(
    null,
  ) as React.RefObject<HTMLElement>;

  useEffect(() => {
    if (!ref.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    resizeObserver.observe(ref.current);

    return () => resizeObserver.disconnect();
  }, []);

  const onSubmit = () => {
    onAddData(colName || "", tempValue);
    setValue(`rows.${rowIndex}.${colName}`, tempValue);
  };

  useOnClickOutside(ref, () => {
    setState("idle");
    setTempValue(value || ""); // Reset to original value on outside click
  });

  return (
    <td
      ref={ref as React.RefObject<HTMLTableCellElement>}
      className={cn(
        "border-b border-neutral bg-neutral-object border-l p-3 text-sm relative",
      )}
    >
      <div className="invisible">{value || "Escribe un texto"}</div>
      <div
        className={cn(
          "absolute bg-neutral-object top-1.5 z-50",
          containerWidth < 180 ? "right-4" : "left-3 right-0",
        )}
      >
        <Input
          type="text"
          variant="xs"
          placeholder={placeholder || "Escribe un texto..."}
          value={tempValue}
          maxLength={isNumberInput ? 10 : 250}
          onChange={(e) => {
            if (isDecimalInput) {
              // Use regex to match valid number format with max 4 decimal places
              const match = /^\d*\.?\d{0,4}/.exec(e.target.value);
              const value = match ? match[0] : "";
              setTempValue(value);
            } else if (isNumberInput) {
              const value = e.target.value;
              if (!value || /^\d+$/.test(value)) {
                setTempValue(value);
              }
            } else {
              setTempValue(e.target.value);
            }
          }}
          className="max-w-[320px] shadow-action-md"
        />
        <div
          className={cn(
            "absolute translate-y-0.5 bg-neutral-object shadow-action-md rounded-lg",
          )}
        >
          <Button
            group="left"
            size="icon"
            variant="tertiary"
            aria-label="Cancelar edición"
            onClick={() => setState("idle")}
          >
            <Icon type="far" name="times" />
          </Button>
          <Button
            group="right"
            size="icon"
            variant="tertiary"
            disabled={!tempValue || tempValue.trim().length === 0}
            aria-label="Guardar cambios"
            onClick={() => {
              onSubmit();
              setState("idle");
            }}
          >
            <Icon type="far" name="check" />
          </Button>
        </div>
      </div>
    </td>
  );
};
